import React, { useEffect, useRef, useState } from "react"
import CMSRender from "aldoo-ra/CMS/cms-render"
import Button from "aldoo-ra/Button"
import DynamicSVG from "aldoo-ra/DynamicSVG"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Icon from "aldoo-ra/Icon"
import ContentDistancer from "./ContentDistancer"
import MenuGroup from "aldoo-ra/MenuGroup"
import useSitePlatform from "@components/Site/useSitePlatform"

const MOBILE_BREAKPOINT = 768
const headerHideAt = 80
const minDistanceToBottom = 50 // Minimum distance to bottom before hiding header

const Features = () => {
  const [isFixed, setIsFixed] = useState(false)
  const [headerVisible, setHeaderVisible] = useState(true)
  const [isMobile, setIsMobile] = useState(false)
  const [topOffset, setTopOffset] = useState(80) // Default to navbar height
  const headerRef = useRef(null)
  const containerRef = useRef(null)
  const listRef = useRef(null)
  const texts = TextBundle({ id: "features_comparison" })

  useEffect(() => {
    const updateTopOffset = () => {
      const width = window.innerWidth;
      
      // For sm, md: ArrowMenuMobile only (80px)
      if (width < 1079) {
        setTopOffset(80);
      }
      // For lg: ArrowMenuMobile + Navbar (160px)
      else if (width >= 1080 && width < 1279) {
        setTopOffset(160);
      }
      // For xl, 2xl: Navbar only (80px)
      else {
        setTopOffset(80);
      }
    };

    // Initial calculation
    updateTopOffset();

    // Add resize listener
    window.addEventListener('resize', updateTopOffset);
    
    return () => window.removeEventListener('resize', updateTopOffset);
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current || !listRef.current || !headerRef.current)
        return

      const containerRect = containerRef.current.getBoundingClientRect()
      const headerHeight = headerRef.current.offsetHeight

      // Find the last feature item in the list
      const listItems = listRef.current.querySelectorAll(
        ".grid.grid-cols-12.lg\\:py-4"
      )
      const lastItem = listItems[listItems.length - 1]

      if (!lastItem) return

      const lastItemRect = lastItem.getBoundingClientRect()

      // Use the topOffset value as the threshold
      const positionThreshold = 0

      // Check if we should fix the header
      if (containerRect.top <= positionThreshold) {
        setIsFixed(true)

        // Calculate distance from header bottom to last item bottom
        const distanceToBottom =
          lastItemRect.bottom - (positionThreshold + headerHeight)

        // Fade out header when close to bottom
        setHeaderVisible(distanceToBottom > minDistanceToBottom)
      } else {
        setIsFixed(false)
        setHeaderVisible(true)
      }
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [isFixed])

  const [platform, setPlatform] = useSitePlatform()

  // Create a function to sort features by priority
  const preProcessFeatures = (data) => {
    if (!Array.isArray(data)) return [];
    return [...data].sort((a, b) => a.priority - b.priority);
  };

  return (
    <div className="flex">
      <div
        ref={containerRef}
        className="relative w-[330px] md:w-[460px] lg:w-[720px] xl:w-[828px] max-w-[460px] md:max-w-full bg-theme_white rounded-[24px] shadow-custom_card overflow-hidden pb-8 mt-20 md:mb-[200px] mx-auto"
      >
        {/* Premium column background */}
        <div className="absolute top-0 right-0 w-[calc(25%+16px)] lg:w-[calc(25%+48px)] min-w-[105px] h-full bg-primary_lt rounded-[24px]" />

        {/* Fixed Header */}
        <div
          ref={headerRef}
          style={{
            position: isFixed ? "fixed" : "relative",
            top: isFixed ? `${topOffset}px` : "auto", // Use the calculated topOffset
            width: isFixed ? containerRef.current?.offsetWidth || "auto" : "auto",
            zIndex: 5,
            opacity: headerVisible ? 1 : 0,
            visibility: headerVisible ? "visible" : "hidden",
            transition: "opacity 0.3s ease, visibility 0.3s ease",
          }}
          className="grid grid-cols-12 bg-theme_white border-b border-primary px-4 lg:px-10 xl:px-12 pt-4 md:pt-6"
        >
          {/* Premium column background */}
          <div className="absolute top-0 right-0 w-[calc(25%+16px)] lg:w-[calc(25%+48px)] min-w-[105px] h-full bg-primary_lt rounded-tl-[24px] -z-10" />

          {/* Features */}
          <Typography variant="subtitle_30_accent" className="col-span-4 md:col-span-6">
            {texts?.features}
          </Typography>

          {/* Free plan title */}
          <div className="flex flex-col items-center col-span-4 md:col-span-3 min-w-[105px] text-left py-4 pl-6 md:pl-0 lg:pr-12">
            <Typography variant="subtitle_30_accent" className="text-gray_2 my-0">
              {texts?.free}
            </Typography>

            <Typography variant="body_24" className="text-text my-0">
              {texts?.free_price}
            </Typography>
          </div>

          {/* Premium plan title */}
          <div className="flex flex-col items-center col-span-4 md:col-span-3 min-w-[105px] text-left py-4 pl-4">
            <Typography
              variant="subtitle_30_accent"
              className="text-primary my-0"
            >
              {texts?.premium}
            </Typography>

            <Typography variant="body_24" className="text-text my-0">
              {texts?.premium_price}
            </Typography>
          </div>
        </div>

        {/* Spacer div when header is fixed */}
        {isFixed && (
          <div style={{ height: headerRef.current?.offsetHeight || 0 }} />
        )}

        {/* Features List */}
        <div ref={listRef} className="relative px-4 lg:px-10 xl:px-12">
          <CMSRender
            contentType="AppFeatureComparison"
            sort={{ priority: 1 }}
            render={(data) => {
              const sortedFeatures = preProcessFeatures(data);
              
              return sortedFeatures.map((feature, index) => (
                <div key={index} className="grid grid-cols-12 lg:py-4 border-b border-gray_3">
                  {/* Feature Icon and Name */}
                  <div className="col-span-4 min-w-[130px] md:col-span-6 flex flex-row items-center gap-2">
                    <DynamicSVG
                      url={feature.icon}
                      className="absolute left-3 lg:left-9 xl:left-11 w-5 md:w-6 lg:w-7 h-5 md:h-6 lg:h-7 text-primary"
                    />

                    <Typography 
                      variant="body_24_minor" 
                      className="ml-6 md:ml-7 lg:ml-9 xl:ml-10 text-text"
                    >
                      {feature.name}
                    </Typography>
                  </div>

                  {/* Free Plan Column */}
                  <div className="col-span-4 md:col-span-3 min-w-[105px] flex items-center justify-center pl-6 md:pl-0 lg:pr-12">
                    {feature.free ? (
                      <Icon
                        id="check_bold"
                        className="h-3 lg:h-5 text-primary_lt"
                      />
                    ) : (
                      <span className="text-[18px] lg:text-[24px] font-bold text-gray_1">
                        —
                      </span>
                    )}
                  </div>

                  {/* Premium Plan Column */}
                  <div className="col-span-4 md:col-span-3 min-w-[105px] flex items-center justify-center pl-5">
                    {feature.premium ? (
                      <Icon
                        id="check_bold"
                        className="h-3 lg:h-5 text-primary_lt"
                      />
                    ) : (
                      <span className="text-[18px] lg:text-[24px] font-bold text-gray_1">
                        —
                      </span>
                    )}
                  </div>
                </div>
              ));
            }}
          />

          {/* Footer */}
          <div className="relative grid grid-cols-12 pt-8 items-center">
            <div className="col-span-6"></div>

            {/* Download btn */}
            <div className="col-span-3 flex justify-end pr-4 md:pr-1 lg:pr-12 xl:pr-14">
              <div className="relative z-10">
                <MenuGroup
                  name="download-feature_table"
                  renderAs="hlist"
                  itemsAsLinks={false}
                  styleOverrides={{
                    menu_item: "p-0 m-0",
                    menu: "p-0 m-0",
                  }}
                  menuItemRender={(item) => {
                    const url =
                      (item?.link[0]?.parameters &&
                        item?.link[0]?.parameters[platform]) ||
                      item?.link[0]?.url
                    return (
                      <Button 
                        className="w-auto h-[40px] md:h-[44px] lg:h-[48px] flex items-center justify-center text-primary hover:text-primary_hov text-[14px] md:text-[16px] lg:text-[18px] xl:text-[20px] 2xl:text-[24px] font-normal rounded-full transition"
                      >
                        <a href={url} target="_blank">
                          {item?.label}
                        </a>
                      </Button>
                    )
                  }}
                />
              </div>
            </div>

            {/* Buy Now btn */}
            <div className="col-span-3 flex justify-center relative lg:pl-3 z-10">
              <Button 
                variant="primary_s"
                className="min-w-[88px] 2xl:text-[24px]"
              >
                {texts?.cta}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Invisible div for distancing the side menu from the cards */}
      <ContentDistancer height="500px"/>
    </div>
  )
}

export default Features